import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  TrendingUp,
  TrendingDown,
  Target,
  Award,
  Users,
  DollarSign,
  ShoppingCart,
  Calendar,
  BarChart3,
  PieChart,
  Activity,
  Star,
  Trophy,
  Zap,
  ArrowUp,
  ArrowDown,
  Minus
} from 'lucide-react';
import apiService from '../services/api';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import toast from 'react-hot-toast';

// Chart components for visualizations
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
  RadialLinearScale
} from 'chart.js';
import { Line, Bar, Doughnut, Radar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
  RadialLinearScale
);

const SalesExecutivePerformance = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [performanceData, setPerformanceData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('current_month');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);

  useEffect(() => {
    fetchPerformanceData();
  }, [selectedPeriod, selectedYear, selectedMonth]);

  const fetchPerformanceData = async () => {
    try {
      setLoading(true);

      const params = {
        period: selectedPeriod,
        year: selectedYear,
        month: selectedMonth
      };

      const response = await apiService.get('/salesman/performance', { params });
      setPerformanceData(response);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching performance data:', error);
      toast.error('Failed to load performance data');
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const getPerformanceColor = (value, target) => {
    const percentage = (value / target) * 100;
    if (percentage >= 100) return 'text-green-600';
    if (percentage >= 80) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPerformanceIcon = (value, target) => {
    const percentage = (value / target) * 100;
    if (percentage >= 100) return <ArrowUp className="w-4 h-4 text-green-600" />;
    if (percentage >= 80) return <Minus className="w-4 h-4 text-yellow-600" />;
    return <ArrowDown className="w-4 h-4 text-red-600" />;
  };

  const getPerformanceTrend = (current, previous) => {
    if (!previous) return { value: 0, direction: 'neutral' };
    const change = ((current - previous) / previous) * 100;
    return {
      value: Math.abs(change),
      direction: change > 0 ? 'up' : change < 0 ? 'down' : 'neutral'
    };
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!performanceData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500">No performance data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Performance Indicators</h1>
            <p className="mt-2 text-gray-600">
              Track your sales performance, targets, and achievements
            </p>
          </div>

          {/* Period Selector */}
          <div className="flex items-center space-x-4">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="current_month">Current Month</option>
              <option value="last_month">Last Month</option>
              <option value="current_quarter">Current Quarter</option>
              <option value="current_year">Current Year</option>
            </select>

            {selectedPeriod === 'current_month' && (
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(2024, i).toLocaleDateString('en-US', { month: 'long' })}
                  </option>
                ))}
              </select>
            )}

            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              {Array.from({ length: 5 }, (_, i) => {
                const year = new Date().getFullYear() - 2 + i;
                return (
                  <option key={year} value={year}>
                    {year}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
      </div>

      {/* KPI Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Revenue KPI */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(performanceData.totalRevenue || 0)}
              </p>
              <div className="flex items-center mt-2">
                {getPerformanceIcon(performanceData.totalRevenue || 0, performanceData.revenueTarget || 1)}
                <span className={`text-sm font-medium ml-1 ${getPerformanceColor(performanceData.totalRevenue || 0, performanceData.revenueTarget || 1)
                  }`}>
                  {formatPercentage((performanceData.totalRevenue || 0) / (performanceData.revenueTarget || 1))}
                </span>
              </div>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Orders KPI */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">
                {performanceData.totalOrders || 0}
              </p>
              <div className="flex items-center mt-2">
                {getPerformanceIcon(performanceData.totalOrders || 0, performanceData.orderTarget || 1)}
                <span className={`text-sm font-medium ml-1 ${getPerformanceColor(performanceData.totalOrders || 0, performanceData.orderTarget || 1)
                  }`}>
                  {formatPercentage((performanceData.totalOrders || 0) / (performanceData.orderTarget || 1))}
                </span>
              </div>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <ShoppingCart className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Customers KPI */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">New Customers</p>
              <p className="text-2xl font-bold text-gray-900">
                {performanceData.newCustomers || 0}
              </p>
              <div className="flex items-center mt-2">
                {getPerformanceIcon(performanceData.newCustomers || 0, performanceData.customerTarget || 1)}
                <span className={`text-sm font-medium ml-1 ${getPerformanceColor(performanceData.newCustomers || 0, performanceData.customerTarget || 1)
                  }`}>
                  {formatPercentage((performanceData.newCustomers || 0) / (performanceData.customerTarget || 1))}
                </span>
              </div>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Performance Score KPI */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Performance Score</p>
              <p className="text-2xl font-bold text-gray-900">
                {performanceData.performanceScore || 0}%
              </p>
              <div className="flex items-center mt-2">
                <Star className="w-4 h-4 text-yellow-600" />
                <span className="text-sm font-medium ml-1 text-gray-600">
                  {performanceData.performanceTier || 'Standard'}
                </span>
              </div>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Trophy className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Performance Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Revenue Trend Chart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trend</h3>
          <div className="h-64">
            <Line
              data={{
                labels: performanceData.revenueByMonth?.map(item => item.month) || [],
                datasets: [
                  {
                    label: 'Revenue',
                    data: performanceData.revenueByMonth?.map(item => item.revenue) || [],
                    borderColor: 'rgb(59, 130, 246)',
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    tension: 0.4,
                    fill: true
                  }
                ]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: function (value) {
                        return formatCurrency(value);
                      }
                    }
                  }
                }
              }}
            />
          </div>
        </div>

        {/* Performance Breakdown */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Breakdown</h3>
          <div className="h-64">
            <Doughnut
              data={{
                labels: ['Revenue', 'Orders', 'Customers'],
                datasets: [
                  {
                    data: [
                      performanceData.totalRevenue || 0,
                      performanceData.totalOrders || 0,
                      performanceData.newCustomers || 0
                    ],
                    backgroundColor: [
                      'rgba(59, 130, 246, 0.8)',
                      'rgba(34, 197, 94, 0.8)',
                      'rgba(147, 51, 234, 0.8)'
                    ],
                    borderWidth: 2,
                    borderColor: '#fff'
                  }
                ]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom'
                  }
                }
              }}
            />
          </div>
        </div>
      </div>

      {/* Detailed Performance Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Target vs Achievement */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Target vs Achievement</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Revenue</span>
                <span className="text-sm text-gray-600">
                  {formatCurrency(performanceData.totalRevenue || 0)} / {formatCurrency(performanceData.revenueTarget || 0)}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full"
                  style={{ width: `${Math.min(100, ((performanceData.totalRevenue || 0) / (performanceData.revenueTarget || 1)) * 100)}%` }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Orders</span>
                <span className="text-sm text-gray-600">
                  {performanceData.totalOrders || 0} / {performanceData.orderTarget || 0}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full"
                  style={{ width: `${Math.min(100, ((performanceData.totalOrders || 0) / (performanceData.orderTarget || 1)) * 100)}%` }}
                ></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Customers</span>
                <span className="text-sm text-gray-600">
                  {performanceData.newCustomers || 0} / {performanceData.customerTarget || 0}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-purple-600 h-2 rounded-full"
                  style={{ width: `${Math.min(100, ((performanceData.newCustomers || 0) / (performanceData.customerTarget || 1)) * 100)}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Top Customers */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Customers</h3>
          <div className="space-y-3">
            {performanceData.topCustomers?.slice(0, 5).map((customer, index) => (
              <div key={customer.customerId} className="flex items-center justify-between">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-sm font-medium text-gray-700">
                    {index + 1}
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">
                      {customer.customer?.businessName || customer.customer?.name || 'Unknown Customer'}
                    </p>
                    <p className="text-xs text-gray-500">{customer.orderCount || 0} orders</p>
                  </div>
                </div>
                <span className="text-sm font-medium text-gray-900">
                  {formatCurrency(customer.totalRevenue || 0)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Performance Insights */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Insights</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center">
                <TrendingUp className="w-5 h-5 text-blue-600 mr-2" />
                <span className="text-sm font-medium text-blue-900">Best Performing Day</span>
              </div>
              <span className="text-sm font-medium text-blue-900">
                {performanceData.bestPerformingDay || 'N/A'}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center">
                <Award className="w-5 h-5 text-green-600 mr-2" />
                <span className="text-sm font-medium text-green-900">Conversion Rate</span>
              </div>
              <span className="text-sm font-medium text-green-900">
                {formatPercentage(performanceData.conversionRate || 0)}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
              <div className="flex items-center">
                <Activity className="w-5 h-5 text-purple-600 mr-2" />
                <span className="text-sm font-medium text-purple-900">Activity Score</span>
              </div>
              <span className="text-sm font-medium text-purple-900">
                {performanceData.activityScore || 0}/100
              </span>
            </div>

            <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
              <div className="flex items-center">
                <Zap className="w-5 h-5 text-yellow-600 mr-2" />
                <span className="text-sm font-medium text-yellow-900">Efficiency Rating</span>
              </div>
              <span className="text-sm font-medium text-yellow-900">
                {performanceData.efficiencyRating || 'N/A'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesExecutivePerformance;


